/**
 * DataTable — Sortable, responsive data table
 *
 * Column headers with sort arrows, alternating row backgrounds,
 * truncated text with tooltip, sticky first column on mobile.
 */
import React, { useState, useMemo } from 'react';
import type { DataTableData } from '../types';

interface DataTableProps {
  data: DataTableData;
}

type SortDir = 'asc' | 'desc' | null;

export const DataTable: React.FC<DataTableProps> = ({ data }) => {
  const { columns, rows, caption } = data;
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const handleSort = (key: string, sortable?: boolean) => {
    if (!sortable) return;
    if (sortKey === key) {
      // Cycle: asc → desc → none
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortKey(null);
        setSortDir(null);
      }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    return [...rows].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [rows, sortKey, sortDir]);

  if (!columns || columns.length === 0) return null;

  const alignClass = (align?: 'left' | 'center' | 'right') =>
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  return (
    <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/20 shadow-[0_0_20px_rgba(34,211,238,0.08)] overflow-hidden">
      {/* Caption */}
      {caption && (
        <div className="px-4 py-3 border-b border-cyan-400/10">
          <p className="text-xs font-medium text-cyan-400/60">{caption}</p>
        </div>
      )}

      {/* Scrollable table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key, col.sortable)}
                  className={`px-4 py-3 text-[10px] uppercase tracking-wider font-bold border-b border-cyan-400/10 select-none ${alignClass(
                    col.align
                  )} ${
                    idx === 0
                      ? 'sticky left-0 z-10 bg-black/80 backdrop-blur-sm min-w-[120px]'
                      : ''
                  } ${
                    col.sortable
                      ? 'cursor-pointer hover:text-cyan-300 transition-colors duration-150'
                      : ''
                  } ${
                    sortKey === col.key ? 'text-cyan-300' : 'text-cyan-400/50'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      <span className="inline-flex flex-col text-[8px] leading-none">
                        <span
                          className={`${
                            sortKey === col.key && sortDir === 'asc'
                              ? 'text-cyan-400'
                              : 'text-cyan-400/20'
                          }`}
                        >
                          ▲
                        </span>
                        <span
                          className={`${
                            sortKey === col.key && sortDir === 'desc'
                              ? 'text-cyan-400'
                              : 'text-cyan-400/20'
                          }`}
                        >
                          ▼
                        </span>
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={`transition-colors duration-150 ${
                  rowIdx % 2 === 1 ? 'bg-white/[0.02]' : ''
                } hover:bg-cyan-400/[0.04]`}
              >
                {columns.map((col, colIdx) => {
                  const value = row[col.key];
                  const display =
                    value == null
                      ? '—'
                      : typeof value === 'number'
                        ? value.toLocaleString()
                        : String(value);
                  const isLong = display.length > 40;

                  return (
                    <td
                      key={col.key}
                      title={isLong ? display : undefined}
                      className={`px-4 py-3 text-sm border-b border-cyan-400/5 ${alignClass(
                        col.align
                      )} ${
                        colIdx === 0
                          ? 'sticky left-0 z-10 bg-black/80 backdrop-blur-sm font-medium text-cyan-300 min-w-[120px]'
                          : 'text-cyan-300/70'
                      } ${isLong ? 'max-w-[200px] truncate' : ''}`}
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row count footer */}
      {rows.length > 5 && (
        <div className="px-4 py-2 border-t border-cyan-400/5">
          <span className="text-[10px] text-cyan-400/30">
            {rows.length} rows
          </span>
        </div>
      )}
    </div>
  );
};

export default DataTable;
